import { useState } from "react";
import { toast } from "react-toastify";
import { initFacebookSDK, uploadImage } from "../utils/facebookUtils";
import { savePostLink, markAutoSavedPostPublished } from "../utils/firebaseUtils";

/**
 * Custom hook for publishing functionality
 * @returns {Object} - Publishing methods and state
 */
const usePublishing = () => {
  const [isPublishing, setIsPublishing] = useState(false);

  /**
   * Publish post to Facebook fanpages
   * @param {Object} generatedPost - Generated post
   * @param {Array} selectedFanpages - Selected fanpages
   * @param {boolean} includeContactInfo - Whether to include contact info
   * @param {boolean} allowNoImage - Whether to allow no image
   * @param {string} imageSource - Image source
   * @param {Array} selectedImages - Selected images
   * @param {Array} selectedSerperImages - Selected Serper images
   * @param {string} geminiGeneratedImage - Gemini generated image
   * @param {Function} incrementPostedCount - Function to increment posted count
   * @param {string} autoSavePostId - Auto-saved post ID for tracking
   * @returns {Promise<void>}
   */
  const publishPost = async (
    generatedPost,
    selectedFanpages,
    includeContactInfo,
    allowNoImage,
    imageSource,
    selectedImages,
    selectedSerperImages,
    geminiGeneratedImage,
    incrementPostedCount,
    autoSavePostId = null
  ) => {
    if (selectedFanpages.length === 0) {
      toast.error("Vui lòng chọn ít nhất 1 fanpage để đăng bài!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    const fbAccessToken = localStorage.getItem("fbAccessToken");
    const storedFanpages = JSON.parse(localStorage.getItem("fanpages") || "{}");

    if (!fbAccessToken) {
      toast.error("Vui lòng đăng nhập vào Facebook trước!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsPublishing(true);

    // Thêm biến để theo dõi số lần thử
    let retryCount = 0;
    const maxRetries = 2;

    // Hàm thử lại việc đăng bài
    const tryPublishPost = async () => {
      try {
        await initFacebookSDK();
        let successCount = 0;

        for (const fanpage of selectedFanpages) {
          try {
            // Get fanpage info from localStorage
            const fanpageInfo = storedFanpages[fanpage.id] || {};

            // Create footer with fanpage info
            let footerText = "";

            if (includeContactInfo && fanpageInfo.fanpageInfo) {
              footerText = `

${fanpageInfo.fanpageInfo || ""}`;
            }

            // Combine post content with footer - Sử dụng nội dung hiện tại của bài viết
            const fullPostContent = generatedPost.text + footerText;

            // Upload images only if not text-only post
            let attachedMedia = [];
            if (!allowNoImage) {
              if (imageSource === "upload" && selectedImages.length > 0) {
                // Nếu nguồn ảnh là upload và đã tải lên ảnh
                for (const imgUrl of selectedImages) {
                  const photoId = await uploadImage(imgUrl, fanpage);
                  attachedMedia.push({ media_fbid: photoId });
                }
              } else if (
                imageSource === "internet" &&
                selectedSerperImages.length > 0
              ) {
                // Nếu nguồn ảnh là internet và đã chọn ảnh từ Serper
                for (const imgUrl of selectedSerperImages) {
                  const photoId = await uploadImage(imgUrl, fanpage);
                  attachedMedia.push({ media_fbid: photoId });
                }
              } else if (imageSource === "ai" && geminiGeneratedImage) {
                // Nếu nguồn ảnh là AI và đã tạo được ảnh từ Gemini
                const photoId = await uploadImage(
                  geminiGeneratedImage,
                  fanpage
                );
                attachedMedia.push({ media_fbid: photoId });
              } else if (
                generatedPost.images &&
                generatedPost.images.length > 0
              ) {
                // Nếu có mảng ảnh từ API
                for (const imgUrl of generatedPost.images) {
                  const photoId = await uploadImage(imgUrl, fanpage);
                  attachedMedia.push({ media_fbid: photoId });
                }
              } else if (generatedPost.image) {
                // Nếu không thuộc các trường hợp trên nhưng có ảnh trong bài viết
                const photoId = await uploadImage(generatedPost.image, fanpage);
                attachedMedia.push({ media_fbid: photoId });
              }
            }

            // Prepare post data
            const postData = {
              message: fullPostContent,
              access_token: fanpage.access_token,
            };

            // Add attached_media only if there are images and it's not a text-only post
            if (!allowNoImage && attachedMedia.length > 0) {
              postData.attached_media = attachedMedia;
            }

            // Post to Facebook with combined content
            await new Promise((resolve, reject) => {
              window.FB.api(
                `/${fanpage.id}/feed`,
                "post",
                postData, // Use the prepared postData object
                async function (postResponse) {
                  if (!postResponse || postResponse.error) {
                    reject(postResponse.error);
                  } else {
                    successCount++;
                    // Lưu link bài viết vào Firebase
                    await savePostLink(fanpage.id, postResponse.id);
                    
                    // Mark auto-saved post as published nếu có autoSavePostId
                    if (autoSavePostId) {
                      await markAutoSavedPostPublished(autoSavePostId, {
                        url: `https://www.facebook.com/${fanpage.id}/posts/${postResponse.id}`,
                        platform: "facebook",
                        fanpageId: fanpage.id,
                      });
                    }
                    
                    resolve();
                  }
                }
              );
            });

            toast.success(`Đăng bài thành công lên ${fanpage.name}!`, {
              position: "top-right",
              autoClose: 3000,
            });
          } catch (error) {
            console.error(`Đăng bài thất bại lên ${fanpage.name}:`, error);

            // Nếu là lỗi khi đăng bài với ảnh AI và còn cơ hội thử lại
            if (imageSource === "ai" && retryCount < maxRetries) {
              retryCount++;
              await new Promise((resolve) => setTimeout(resolve, 2000)); // Đợi 2 giây
              return await tryPublishPost(); // Thử lại
            }

            toast.error(`Đăng bài thất bại lên ${fanpage.name}!`, {
              position: "top-right",
              autoClose: 3000,
            });
          }
        }

        // If at least one post was successful, increment the counter
        if (successCount > 0 && incrementPostedCount) {
          await incrementPostedCount();
        }
      } catch (error) {
        console.error("Failed to publish posts:", error);

        // Nếu là lỗi khi đăng bài với ảnh AI và còn cơ hội thử lại
        if (imageSource === "ai" && retryCount < maxRetries) {
          retryCount++;
          console.log(`Thử lại lần ${retryCount}/${maxRetries}...`);
          await new Promise((resolve) => setTimeout(resolve, 2000)); // Đợi 2 giây
          return await tryPublishPost(); // Thử lại
        }
      } finally {
        if (retryCount === maxRetries || retryCount === 0) {
          // Chỉ đặt lại trạng thái khi đã hết số lần thử hoặc thành công ngay lần đầu
          setIsPublishing(false);
        }
      }
    };

    // Gọi hàm thử xuất bản
    await tryPublishPost();
  };

  /**
   * Publish post to user's profile
   * @param {Object} generatedPost - Generated post
   * @param {boolean} allowNoImage - Whether to allow no image
   * @param {string} imageSource - Image source
   * @param {Array} selectedImages - Selected images
   * @param {Array} selectedSerperImages - Selected Serper images
   * @param {string} geminiGeneratedImage - Gemini generated image
   * @param {Function} incrementPostedCount - Function to increment posted count
   * @returns {Promise<void>}
   */
  const publishToProfile = async (
    generatedPost,
    allowNoImage,
    imageSource,
    selectedImages,
    selectedSerperImages,
    geminiGeneratedImage,
    incrementPostedCount
  ) => {
    const fbAccessToken = localStorage.getItem("fbAccessToken");

    if (!fbAccessToken) {
      toast.error("Vui lòng đăng nhập vào Facebook trước!", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsPublishing(true);

    // Thêm biến để theo dõi số lần thử
    let retryCount = 0;
    const maxRetries = 2;

    // Hàm thử lại việc đăng bài
    const tryPublishToProfile = async () => {
      try {
        await initFacebookSDK(); // Add this line before making FB API calls

        let attachedMedia = [];

        // Upload images only if not text-only post
        if (!allowNoImage) {
          if (imageSource === "internet" && selectedSerperImages.length > 0) {
            // Nếu nguồn ảnh là internet và đã chọn ảnh từ Serper
            for (const imgUrl of selectedSerperImages) {
              const photoId = await uploadImage(imgUrl);
              attachedMedia.push({ media_fbid: photoId });
            }
          } else if (imageSource === "upload" && selectedImages.length > 0) {
            // Nếu nguồn ảnh là upload và đã tải lên ảnh
            for (const imgUrl of selectedImages) {
              const photoId = await uploadImage(imgUrl);
              attachedMedia.push({ media_fbid: photoId });
            }
          } else if (imageSource === "ai" && geminiGeneratedImage) {
            // Nếu nguồn ảnh là AI và đã tạo được ảnh từ Gemini
            const photoId = await uploadImage(geminiGeneratedImage);
            attachedMedia.push({ media_fbid: photoId });
          } else if (generatedPost.images && generatedPost.images.length > 0) {
            // Nếu có mảng ảnh từ API
            for (const imgUrl of generatedPost.images) {
              const photoId = await uploadImage(imgUrl);
              attachedMedia.push({ media_fbid: photoId });
            }
          } else if (generatedPost.image) {
            // Nếu không thuộc các trường hợp trên nhưng có ảnh trong bài viết
            const photoId = await uploadImage(generatedPost.image);
            attachedMedia.push({ media_fbid: photoId });
          }
        }

        // Prepare post data
        const postData = {
          message: generatedPost.text,
          access_token: fbAccessToken,
        };

        // Add attached_media only if there are images and it's not a text-only post
        if (!allowNoImage && attachedMedia.length > 0) {
          postData.attached_media = attachedMedia;
        }

        return new Promise((resolve, reject) => {
          window.FB.api(
            `/me/feed`,
            "post",
            postData, // Use the prepared postData object
            function (postResponse) {
              if (!postResponse || postResponse.error) {
                console.error(postResponse?.error || "Lỗi không xác định");
                reject(postResponse?.error || new Error("Lỗi không xác định"));
              } else {
                toast.success("Đăng bài thành công lên tường cá nhân!", {
                  position: "top-right",
                  autoClose: 3000,
                });
                if (incrementPostedCount) {
                  incrementPostedCount();
                }
                resolve(postResponse);
              }
            }
          );
        });
      } catch (error) {
        console.error("Failed to publish to profile:", error);

        // Nếu là lỗi khi đăng bài với ảnh AI và còn cơ hội thử lại
        if (imageSource === "ai" && retryCount < maxRetries) {
          retryCount++;
          console.log(`Thử lại lần ${retryCount}/${maxRetries}...`);
          await new Promise((resolve) => setTimeout(resolve, 2000)); // Đợi 2 giây
          return await tryPublishToProfile(); // Thử lại
        }

        toast.error("Không thể đăng bài lên tường cá nhân!", {
          position: "top-right",
          autoClose: 3000,
        });
      } finally {
        if (retryCount === maxRetries || retryCount === 0) {
          // Chỉ đặt lại trạng thái khi đã hết số lần thử hoặc thành công ngay lần đầu
          setIsPublishing(false);
        }
      }
    };

    // Gọi hàm thử xuất bản
    await tryPublishToProfile();
  };

  return {
    isPublishing,
    publishPost,
    publishToProfile,
  };
};

export default usePublishing;
